import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(event.target as Node)) {
        setGuideDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/95 backdrop-blur-md">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 whitespace-nowrap">
              CSS Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            {/* Home */}
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            {/* Shopping Guide Dropdown */}
            <div ref={guideDropdownRef} className="relative">
              <button
                onClick={() => { setGuideDropdownOpen(!guideDropdownOpen); setDropdownOpen(false); }}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>Shopping Guide</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2">
                  <Link
                    to="/article/cssbuy-spreadsheet-complete-guide"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                    onClick={() => setGuideDropdownOpen(false)}
                  >
                    Complete Guide 2026
                  </Link>
                  <Link
                    to="/article/cssbuy-spreadsheet-complete-guide"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                    onClick={() => setGuideDropdownOpen(false)}
                  >
                    How Spreadsheets Work
                  </Link>
                  <Link
                    to="/article/cssbuy-spreadsheet-complete-guide"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                    onClick={() => setGuideDropdownOpen(false)}
                  >
                    Agent Comparison
                  </Link>
                  <Link
                    to="/article/cssbuy-spreadsheet-complete-guide"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                    onClick={() => setGuideDropdownOpen(false)}
                  >
                    Safe Shopping Tips
                  </Link>
                </div>
              )}
            </div>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuideDropdownOpen(false); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-2 h-2 rounded-full bg-primary-300 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Button */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              CSSBuy Sheet
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu - Enhanced */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-5 border-t border-background-200/70 max-h-[70vh] overflow-y-auto">
            {/* Quick Nav */}
            <div className="pt-4 px-4 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 transition-colors"
              >
                <i className="ri-home-4-line text-lg flex items-center justify-center w-5 h-5 text-primary-500"></i>
                Home
              </Link>
              <Link
                to="/article/cssbuy-spreadsheet-complete-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 transition-colors"
              >
                <i className="ri-book-open-line text-lg flex items-center justify-center w-5 h-5 text-primary-500"></i>
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-shopping-cart-2-line text-lg flex items-center justify-center w-5 h-5"></i>
                CSSBuy Sheet
                <i className="ri-arrow-right-up-line ml-auto"></i>
              </a>
            </div>

            {/* Categories Toggle */}
            <div className="pt-4 px-4">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-lg bg-background-100 text-sm font-semibold text-foreground-700"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line text-lg flex items-center justify-center w-5 h-5 text-foreground-500"></i>
                  Categories (14)
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="mt-1 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-300 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}